import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import {
  CubeIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ShoppingCartIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const InventoryOptimization = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedTab, setSelectedTab] = useState('overview');

  useEffect(() => {
    fetchInventoryData();
    fetchAllProducts();
  }, []);

  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/dashboard/inventory-optimization', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setData(response.data);
      setError(null);
    } catch (error) {
      setError('שגיאה בטעינת נתוני המלאי');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await axios.get('/api/products', {
        params: {
          limit: 1000,
          is_active: true
        }
      });
      setAllProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching all products:', error);
    }
  };

  const formatCurrency = (amount) => {
    // Ensure amount is a valid number
    const numericAmount = Number(amount);
    if (isNaN(numericAmount) || numericAmount === null || numericAmount === undefined) {
      return '₪0';
    }
    
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericAmount);
  };

  const getStockStatusColor = (status) => {
    switch (status) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'urgent': return 'text-orange-600 bg-orange-100';
      case 'low': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'urgent': return 'text-orange-600 bg-orange-100';
      case 'normal': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const tabs = [
    { id: 'overview', name: 'סקירה כללית', icon: ChartBarIcon },
    { id: 'profitAnalysis', name: 'ניתוח רווחיות', icon: CurrencyDollarIcon },
    { id: 'lowStock', name: 'מלאי נמוך', icon: ExclamationTriangleIcon },
    { id: 'overStock', name: 'מלאי עודף', icon: CubeIcon },
    { id: 'velocity', name: 'מהירות מכירה', icon: ArrowTrendingUpIcon },
    { id: 'categories', name: 'ניתוח קטגוריות', icon: ChartBarIcon },
    { id: 'recommendations', name: 'המלצות רכישה', icon: ShoppingCartIcon }
  ];

  // Calculate profit potential for all products
  const calculateProfitAnalysis = () => {
    if (!allProducts || allProducts.length === 0) {
      return [];
    }

    return allProducts.map(product => {
      const costPrice = parseFloat(product.cost_price || 0);
      const priceB2C = parseFloat(product.price_b2c || 0);
      const priceB2B = parseFloat(product.price_b2b || 0);
      const stockB2C = parseInt(product.stock_b2c || 0);
      const stockB2B = parseInt(product.stock_b2b || 0);

      const totalCost = costPrice * (stockB2C + stockB2B);
      const potentialRevenueB2C = priceB2C * stockB2C;
      const potentialRevenueB2B = priceB2B * stockB2B;
      const profitB2C = potentialRevenueB2C - (costPrice * stockB2C);
      const profitB2B = potentialRevenueB2B - (costPrice * stockB2B);
      const totalProfit = profitB2C + profitB2B;
      const totalRevenue = potentialRevenueB2C + potentialRevenueB2B;

      const profitMarginB2C = priceB2C > 0 ? ((priceB2C - costPrice) / priceB2C * 100) : 0;
      const profitMarginB2B = priceB2B > 0 ? ((priceB2B - costPrice) / priceB2B * 100) : 0;

      return {
        id: product.id,
        name: product.name,
        sku: product.sku,
        costPrice,
        totalCost,
        stockB2C,
        stockB2B,
        totalStock: stockB2C + stockB2B,
        potentialRevenueB2C,
        potentialRevenueB2B,
        totalRevenue,
        profitB2C,
        profitB2B,
        totalProfit,
        profitMarginB2C,
        profitMarginB2B
      };
    }).filter(p => p.totalStock > 0).sort((a, b) => b.totalProfit - a.totalProfit);
  };

  // Calculate profit data and totals
  const profitData = calculateProfitAnalysis();
  const totalInventoryCost = profitData.reduce((sum, p) => sum + p.totalCost, 0);
  const totalPotentialRevenue = profitData.reduce((sum, p) => sum + p.totalRevenue, 0);
  const totalPotentialProfit = profitData.reduce((sum, p) => sum + p.totalProfit, 0);
  const totalRevenueB2C = profitData.reduce((sum, p) => sum + p.potentialRevenueB2C, 0);
  const totalRevenueB2B = profitData.reduce((sum, p) => sum + p.potentialRevenueB2B, 0);
  const totalProfitB2C = profitData.reduce((sum, p) => sum + p.profitB2C, 0);
  const totalProfitB2B = profitData.reduce((sum, p) => sum + p.profitB2B, 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="mr-3">
            <h3 className="text-sm font-medium text-red-800">שגיאה</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">אופטימיזציית מלאי</h1>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <CubeIcon className="h-8 w-8 text-blue-600" />
                <div className="mr-4">
                  <p className="text-sm font-medium text-blue-600">סך המוצרים</p>
                  <p className="text-2xl font-bold text-blue-900">{data?.summary?.totalProducts || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                <div className="mr-4">
                  <p className="text-sm font-medium text-green-600">עלות מלאי (השקעה)</p>
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(totalInventoryCost)}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    ערך מכירה: {formatCurrency(totalPotentialRevenue)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                <div className="mr-4">
                  <p className="text-sm font-medium text-red-600">מלאי נמוך</p>
                  <p className="text-2xl font-bold text-red-900">{data?.summary?.lowStockCount || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center">
                <ClockIcon className="h-8 w-8 text-orange-600" />
                <div className="mr-4">
                  <p className="text-sm font-medium text-orange-600">חסר במלאי</p>
                  <p className="text-2xl font-bold text-orange-900">{data?.summary?.outOfStockCount || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id)}
                    className={`${
                      selectedTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <Icon className="h-5 w-5 ml-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {selectedTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Inventory Distribution */}
                  <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">התפלגות מלאי</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'מלאי תקין', value: (data?.summary?.totalProducts || 0) - (data?.summary?.lowStockCount || 0) - (data?.summary?.outOfStockCount || 0), fill: '#10B981' },
                            { name: 'מלאי נמוך', value: data?.summary?.lowStockCount || 0, fill: '#F59E0B' },
                            { name: 'חסר במלאי', value: data?.summary?.outOfStockCount || 0, fill: '#EF4444' }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Category Analysis Chart */}
                  <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">ערך מלאי לפי קטגוריה</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={data?.categoryAnalysis?.slice(0, 6) || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="inventoryValue" fill="#3B82F6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {selectedTab === 'profitAnalysis' && (
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-6">ניתוח רווחיות מלאי</h3>
                
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* Total Cost */}
                  <div className="bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
                    <div className="flex items-center gap-2">
                      <CurrencyDollarIcon className="h-8 w-8 text-orange-600" />
                      <div>
                        <p className="text-xs font-bold text-orange-600">עלות מלאי כולל</p>
                        <p className="text-xl font-bold text-orange-900">
                          {formatCurrency(totalInventoryCost)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Total Revenue */}
                  <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                    <div className="flex items-center gap-2">
                      <ShoppingCartIcon className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="text-xs font-bold text-blue-600">הכנסה פוטנציאלית כוללת</p>
                        <p className="text-xl font-bold text-blue-900">
                          {formatCurrency(totalPotentialRevenue)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Total Profit */}
                  <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                    <div className="flex items-center gap-2">
                      <ArrowTrendingUpIcon className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-xs font-bold text-green-600">רווח פוטנציאלי כולל</p>
                        <p className="text-xl font-bold text-green-900">
                          {formatCurrency(totalPotentialProfit)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Margin */}
                  <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                    <div className="flex items-center gap-2">
                      <ChartBarIcon className="h-8 w-8 text-purple-600" />
                      <div>
                        <p className="text-xs font-bold text-purple-600">שולי רווח %</p>
                        <p className="text-xl font-bold text-purple-900">
                          {totalPotentialRevenue > 0 
                            ? ((totalPotentialProfit / totalPotentialRevenue * 100).toFixed(1) + '%')
                            : '0%'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* B2C vs B2B Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* B2C Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-lg border-2 border-blue-300">
                    <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                      <span className="text-2xl">🛒</span> B2C - קמעונאי
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-sm font-semibold text-gray-700">הכנסה פוטנציאלית:</span>
                        <span className="text-xl font-bold text-blue-700">
                          {formatCurrency(totalRevenueB2C)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-sm font-semibold text-gray-700">רווח פוטנציאלי:</span>
                        <span className="text-xl font-bold text-green-700">
                          {formatCurrency(totalProfitB2C)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-blue-100 rounded-lg">
                        <span className="text-sm font-semibold text-blue-900">שולי רווח:</span>
                        <span className="text-lg font-bold text-blue-900">
                          {totalRevenueB2C > 0 
                            ? ((totalProfitB2C / totalRevenueB2C * 100).toFixed(1) + '%')
                            : '0%'
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* B2B Card */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg border-2 border-purple-300">
                    <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                      <span className="text-2xl">🏢</span> B2B - סיטונאי
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-sm font-semibold text-gray-700">הכנסה פוטנציאלית:</span>
                        <span className="text-xl font-bold text-purple-700">
                          {formatCurrency(totalRevenueB2B)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-sm font-semibold text-gray-700">רווח פוטנציאלי:</span>
                        <span className="text-xl font-bold text-green-700">
                          {formatCurrency(totalProfitB2B)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-purple-100 rounded-lg">
                        <span className="text-sm font-semibold text-purple-900">שולי רווח:</span>
                        <span className="text-lg font-bold text-purple-900">
                          {totalRevenueB2B > 0 
                            ? ((totalProfitB2B / totalRevenueB2B * 100).toFixed(1) + '%')
                            : '0%'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Products Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">מוצר</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">מק"ט</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">עלות יחידה</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">מלאי B2C</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">פוטנציאל B2C</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">רווח B2C</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">מלאי B2B</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">פוטנציאל B2B</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">רווח B2B</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase bg-green-50">רווח כולל</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {profitData.slice(0, 50).map((product, index) => (
                        <tr key={product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 max-w-[200px]">
                            <div className="text-sm font-semibold text-gray-900 truncate" title={product.name}>
                              {product.name}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                              {product.sku}
                            </code>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                            ₪{product.costPrice.toFixed(2)}
                          </td>
                          
                          {/* B2C */}
                          <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-blue-700">
                            {product.stockB2C}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium text-blue-600">
                            ₪{product.potentialRevenueB2C.toFixed(0)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold">
                            <span className={product.profitB2C > 0 ? 'text-green-600' : 'text-gray-400'}>
                              ₪{product.profitB2C.toFixed(0)}
                            </span>
                            <div className="text-[10px] text-gray-500">
                              {product.profitMarginB2C.toFixed(1)}%
                            </div>
                          </td>
                          
                          {/* B2B */}
                          <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-700">
                            {product.stockB2B}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium text-purple-600">
                            ₪{product.potentialRevenueB2B.toFixed(0)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold">
                            <span className={product.profitB2B > 0 ? 'text-green-600' : 'text-gray-400'}>
                              ₪{product.profitB2B.toFixed(0)}
                            </span>
                            <div className="text-[10px] text-gray-500">
                              {product.profitMarginB2B.toFixed(1)}%
                            </div>
                          </td>
                          
                          {/* Total Profit */}
                          <td className="px-4 py-3 whitespace-nowrap text-center bg-green-50">
                            <div className="text-lg font-bold text-green-700">
                              ₪{product.totalProfit.toFixed(0)}
                            </div>
                            <div className="text-[10px] text-gray-600">
                              {product.totalStock} יח׳
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                      <tr className="font-bold">
                        <td colSpan="2" className="px-4 py-4 text-right text-sm font-bold text-gray-900">
                          סה"כ ({profitData.length} מוצרים):
                        </td>
                        <td className="px-4 py-4 text-center text-sm font-bold text-orange-700">
                          ₪{totalInventoryCost.toFixed(0)}
                        </td>
                        <td colSpan="2" className="px-4 py-4 text-center text-sm font-bold text-blue-700">
                          ₪{profitData.reduce((sum, p) => sum + p.potentialRevenueB2C, 0).toFixed(0)}
                        </td>
                        <td className="px-4 py-4 text-center text-sm font-bold text-green-700">
                          ₪{profitData.reduce((sum, p) => sum + p.profitB2C, 0).toFixed(0)}
                        </td>
                        <td className="px-4 py-4 text-center text-sm font-bold text-purple-700">
                          ₪{profitData.reduce((sum, p) => sum + p.potentialRevenueB2B, 0).toFixed(0)}
                        </td>
                        <td className="px-4 py-4 text-center text-sm font-bold text-green-700">
                          ₪{profitData.reduce((sum, p) => sum + p.profitB2B, 0).toFixed(0)}
                        </td>
                        <td className="px-4 py-4 text-center bg-green-100">
                          <div className="text-xl font-bold text-green-800">
                            ₪{totalPotentialProfit.toFixed(0)}
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <h4 className="font-semibold text-blue-900 mb-2">💡 הסבר החישובים:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li><strong>פוטנציאל B2C:</strong> מחיר B2C × מלאי B2C</li>
                    <li><strong>רווח B2C:</strong> פוטנציאל B2C - (עלות × מלאי B2C)</li>
                    <li><strong>פוטנציאל B2B:</strong> מחיר B2B × מלאי B2B</li>
                    <li><strong>רווח B2B:</strong> פוטנציאל B2B - (עלות × מלאי B2B)</li>
                    <li><strong>רווח כולל:</strong> סך כל הרווחים מכל המלאי</li>
                  </ul>
                </div>
              </div>
            )}

            {selectedTab === 'lowStock' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">מוצרים עם מלאי נמוך</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מוצר</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מלאי נוכחי</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מלאי מינימלי</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מכירות יומיות</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ימי מלאי</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">סטטוס</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data?.lowStockProducts?.map((product) => (
                        <tr key={product.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              <div className="text-sm text-gray-500">{product.sku}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.currentStock}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.minStockLevel}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.avgDailySales}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.daysOfStock}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStockStatusColor(product.stockStatus)}`}>
                              {product.stockStatus === 'critical' ? 'קריטי' :
                               product.stockStatus === 'urgent' ? 'דחוף' :
                               product.stockStatus === 'low' ? 'נמוך' : 'תקין'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedTab === 'overStock' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">מוצרים עם מלאי עודף</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מוצר</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מלאי נוכחי</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מכירות יומיות</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ימי מלאי</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">הון קפוא</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data?.overStockProducts?.map((product) => (
                        <tr key={product.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              <div className="text-sm text-gray-500">{product.sku}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.currentStock}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.avgDailySales}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.daysOfStock}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                            {formatCurrency(product.tiedUpCapital || (product.currentStock * (product.cost_price || product.costPrice || 0)))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedTab === 'velocity' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">מוצרים מהירי מכירה</h3>
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מוצר</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מהירות</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מלאי</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {data?.fastMovingProducts?.slice(0, 10).map((product) => (
                            <tr key={product.id}>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-green-600 font-medium">
                                {product.velocityScore}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {product.currentStock}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">מוצרים איטיי מכירה</h3>
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מוצר</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מכירות 30 יום</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">הון קפוא</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {data?.slowMovingProducts?.slice(0, 10).map((product) => (
                            <tr key={product.id}>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-red-600">
                                {product.totalSold30d}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-red-600">
                                {formatCurrency(product.tiedUpCapital || (product.currentStock * (product.cost_price || product.costPrice || 0)))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedTab === 'categories' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">ניתוח קטגוריות</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">קטגוריה</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מוצרים</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ערך מלאי</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מלאי נמוך</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">חסר במלאי</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מכירות 30 יום</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data?.categoryAnalysis?.map((category) => (
                        <tr key={category.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {category.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {category.totalProducts}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(category.inventoryValue)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600">
                            {category.lowStockCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                            {category.outOfStockCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                            {category.totalSold30d}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedTab === 'recommendations' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">המלצות רכישה</h3>
                <div className="mb-4 p-4 bg-blue-50 rounded-lg flex flex-wrap gap-6">
                  <p className="text-sm text-blue-700">
                    סך עלות רכישה מוערכת: <span className="font-bold">
                      {formatCurrency(data?.purchaseRecommendations?.reduce((sum, item) => sum + item.estimatedCost, 0) || 0)}
                    </span>
                  </p>
                  <p className="text-sm text-blue-700">
                    מוצרים להזמנה: <span className="font-bold">{data?.purchaseRecommendations?.length || 0}</span>
                  </p>
                  <p className="text-xs text-blue-500 self-center">* מבוסס על ממוצע מכירות 3 חודשים אחרונים</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מוצר</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מלאי נוכחי</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מכירות חודשי (ממוצע)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ימי מלאי</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">כמות מומלצת</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">עלות מוערכת</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">עדיפות</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data?.purchaseRecommendations?.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{item.name}</div>
                              <div className="text-sm text-gray-500">{item.sku}</div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.currentStock}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              <span className="font-medium">{item.avgMonthlySales || 0}</span>
                              <span className="text-xs text-gray-500 mr-1">/ חודש</span>
                            </div>
                            <div className="text-xs text-gray-400">נמכרו {item.totalSold90d || 0} ב-3 חודשים</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <span className={`font-medium ${
                              item.daysOfStock === 999 ? 'text-gray-400' :
                              item.daysOfStock <= 7 ? 'text-red-600' :
                              item.daysOfStock <= 14 ? 'text-orange-500' :
                              item.daysOfStock <= 30 ? 'text-yellow-600' :
                              'text-green-600'
                            }`}>
                              {item.daysOfStock >= 999 ? 'אין מכירות' : `${item.daysOfStock} ימים`}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            {item.recommendedQuantity}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.estimatedCost)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(item.priority)}`}>
                              {item.priority === 'critical' ? 'קריטי' :
                               item.priority === 'urgent' ? 'דחוף' :
                               item.priority === 'normal' ? 'רגיל' : 'נמוך'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryOptimization;
